/**
 * Centralized GA4 analytics utility for Gestoria Lite.
 *
 * All custom events are sent via window.gtag which is loaded in index.html.
 * Google Consent Mode v2 handles cookie gating automatically — we always
 * fire events regardless of consent status.
 */

/* ── Event names (single source of truth) ────────────────────── */

export const GA_EVENTS = {
  // Landing page engagement
  SCROLL_DEPTH: 'scroll_depth',
  CTA_CLICK: 'cta_click',
  PRICING_VIEW: 'pricing_view',
  FAQ_INTERACTION: 'faq_interaction',

  // Signup funnel
  SIGNUP_FORM_START: 'signup_form_start',
  SIGNUP_FORM_SUBMIT: 'signup_form_submit',
  SIGNUP_FORM_ERROR: 'signup_form_error',
  CONSENT_CHECKBOX_TOGGLE: 'consent_checkbox_toggle',

  // Authentication
  MAGIC_LINK_REQUESTED: 'magic_link_requested',
  MAGIC_LINK_VERIFIED: 'magic_link_verified',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_ERROR: 'login_error',

  // Trial activation
  CLIENT_CREATED: 'client_created',
  PORTAL_LINK_SHARED: 'portal_link_shared',
  CHECKLIST_CREATED: 'checklist_created',

  // Subscription intent
  PRICING_PAGE_VIEW: 'pricing_page_view',
  PLAN_SELECTED: 'plan_selected',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',

  // Cookie consent
  COOKIE_CONSENT_ACCEPTED: 'cookie_consent_accepted',
  COOKIE_CONSENT_REJECTED: 'cookie_consent_rejected',

  // Onboarding
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_DISMISSED: 'onboarding_dismissed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
} as const;

export type AnalyticsEvent = (typeof GA_EVENTS)[keyof typeof GA_EVENTS];

/* ── Parameter types per event ───────────────────────────────── */

export interface AnalyticsEventParams {
  [GA_EVENTS.SCROLL_DEPTH]: { percent: number };
  [GA_EVENTS.CTA_CLICK]: { location: string };
  [GA_EVENTS.PRICING_VIEW]: Record<string, never>;
  [GA_EVENTS.FAQ_INTERACTION]: { question_index: number; question_text: string };

  [GA_EVENTS.SIGNUP_FORM_START]: Record<string, never>;
  [GA_EVENTS.SIGNUP_FORM_SUBMIT]: { page: 'landing' | 'login' };
  [GA_EVENTS.SIGNUP_FORM_ERROR]: { error_message: string; page: 'landing' | 'login' };
  [GA_EVENTS.CONSENT_CHECKBOX_TOGGLE]: { checkbox: 'privacy' | 'dpa'; checked: boolean };

  [GA_EVENTS.MAGIC_LINK_REQUESTED]: Record<string, never>;
  [GA_EVENTS.MAGIC_LINK_VERIFIED]: Record<string, never>;
  [GA_EVENTS.LOGIN_SUCCESS]: Record<string, never>;
  [GA_EVENTS.LOGIN_ERROR]: { error_type: string };

  [GA_EVENTS.CLIENT_CREATED]: { client_type: string };
  [GA_EVENTS.PORTAL_LINK_SHARED]: { source: 'client_detail' | 'clients_list' };
  [GA_EVENTS.CHECKLIST_CREATED]: { item_count: number };

  [GA_EVENTS.PRICING_PAGE_VIEW]: Record<string, never>;
  [GA_EVENTS.PLAN_SELECTED]: { plan_name: string; action: string };
  [GA_EVENTS.CHECKOUT_STARTED]: { plan_name: string; interval: string };
  [GA_EVENTS.SUBSCRIPTION_ACTIVATED]: Record<string, never>;

  [GA_EVENTS.COOKIE_CONSENT_ACCEPTED]: Record<string, never>;
  [GA_EVENTS.COOKIE_CONSENT_REJECTED]: Record<string, never>;

  [GA_EVENTS.ONBOARDING_STEP_COMPLETED]: { step: string; step_number: number };
  [GA_EVENTS.ONBOARDING_DISMISSED]: { completed_steps: number; total_steps: number };
  [GA_EVENTS.ONBOARDING_COMPLETED]: Record<string, never>;
}

/* ── Core tracking function ──────────────────────────────────── */

export function trackEvent<E extends AnalyticsEvent>(
  eventName: E,
  params: AnalyticsEventParams[E],
): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, params);

  if (import.meta.env.DEV) {
    console.log('[Analytics]', eventName, params);
  }
}

/* ── Restore consent on app start (call before any event) ───── */

/**
 * Reads cookie consent from localStorage and immediately updates
 * Google Consent Mode v2 + Meta Pixel. Must be called synchronously
 * at app startup — before any route renders — so that events fired
 * on the very first page (e.g. /login-success) are not dropped.
 */
export function restoreConsentFromStorage(): void {
  const COOKIE_CONSENT_KEY = 'cookie_consent';
  const CONSENT_VERSION = '2';

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return;
    const data = JSON.parse(stored) as { status: string; version: string };
    if (data.version !== CONSENT_VERSION || data.status !== 'accepted') return;

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    }
    if (typeof window.fbq === 'function') {
      window.fbq('consent', 'grant');
    }
  } catch {
    // Corrupt storage — leave consent as denied (safe default)
  }
}

/* ── Scroll depth tracker (for landing page) ─────────────────── */

const SCROLL_THRESHOLDS = [25, 50, 75, 100];

export function trackScrollDepth(): () => void {
  const fired = new Set<number>();
  let ticking = false;
  let cachedScrollHeight = 0;
  let cachedViewportHeight = 0;

  const updateDimensions = () => {
    cachedScrollHeight = document.documentElement.scrollHeight;
    cachedViewportHeight = window.innerHeight;
  };

  // Cache dimensions once and on resize (avoids forced reflow on scroll)
  updateDimensions();

  const onScroll = () => {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      const percent = ((window.scrollY + cachedViewportHeight) / cachedScrollHeight) * 100;

      for (const threshold of SCROLL_THRESHOLDS) {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackEvent(GA_EVENTS.SCROLL_DEPTH, { percent: threshold });
        }
      }
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateDimensions, { passive: true });

  // Check initial position (page might already be scrolled)
  onScroll();

  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', updateDimensions);
  };
}

/* ── Section view tracker (IntersectionObserver) ─────────────── */

export function trackSectionView(
  elementId: string,
  eventName: AnalyticsEvent,
): () => void {
  let observer: IntersectionObserver | null = null;

  // Defer to next tick so the DOM element is available
  const timer = setTimeout(() => {
    const el = document.getElementById(elementId);
    if (!el) return;

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEvent(eventName, {} as AnalyticsEventParams[typeof eventName]);
          observer?.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
  }, 0);

  return () => {
    clearTimeout(timer);
    observer?.disconnect();
  };
}
